/**
 * Références internes cliquables — « #20637 » (ticket) et « OPP-0027 » (opportunité).
 *
 * Demande William (25/09/2026) : « partout où un numéro de ticket apparaît dans
 * l'interface support, il doit être cliquable et nous emmener directement au bon
 * endroit ». Les descriptions, suivis, tâches et notes citent sans cesse d'autres
 * dossiers (« ticket d'étude #20640 », « lié à OPP-0014 ») : un texte qu'on doit
 * recopier dans la recherche pour le suivre est un lien cassé.
 *
 * Un seul repérage pour les trois formes de rendu du portail :
 *  - texte brut        → `splitRefs` / `linkifyRefs` (éléments React)
 *  - markdown          → `remarkRefLinks` (plugin de `MarkdownView`)
 *  - HTML déjà assaini → `linkifyRefsInHtml` (suivis de ticket en `message_html`)
 *
 * Ce qui n'est PAS une référence (et doit le rester) :
 *  - une entité HTML `&#20637;`, une ancre d'URL `…/page#12345`, un code couleur
 *    `#1a3a5c` : le « # » doit être précédé d'un blanc, d'une ponctuation ou du
 *    début du texte, et suivi de chiffres SEULS ;
 *  - `#1`, `#12` (« priorité #1 ») : un numéro de ticket a au moins 3 chiffres ;
 *  - `#1234567` et plus : jamais un ticket (et souvent une couleur).
 */
import React from 'react';
// ⚠️ PAS d'import de react-router-dom ici : les tests du portail le remplacent par
// des doublures virtuelles partielles (`jest.mock('react-router-dom', …, {virtual:true})`).
// Un `Link` importé ici ferait planter tout test d'un écran qui affiche un texte
// contenant « #20637 ». Les liens sont de simples <a data-ref-link> ; la navigation
// interne est assurée par UN écouteur global (`installRefLinkNavigation`) posé une
// fois dans l'application, sous le routeur.

// (préfixe)(référence) — le préfixe est capturé plutôt que testé en arrière
// (lookbehind) pour rester compatible avec tous les navigateurs du parc.
const REF_SOURCE = '(^|[^\\p{L}\\p{N}_&/#-])(#(\\d{3,6})|OPP-(\\d{1,6}))(?![\\p{L}\\p{N}_])';

export function ticketHref(number) {
  return `/tickets/${Number(number)}`;
}

// L'API d'une opportunité accepte son numéro ENTIER (`get_deal` : id OU number) ;
// la fiche bascule ensuite sur l'identifiant réel (CrmDealDetailPage).
export function dealHref(number) {
  return `/crm/deals/${Number(number)}`;
}

/**
 * Découpe un texte en morceaux : `{type:'text', value}`, `{type:'ticket'|'deal',
 * number, label, href}`. Sans référence, rend un seul morceau texte (ou rien).
 */
export function splitRefs(text) {
  if (typeof text !== 'string' || text === '') return [];
  const re = new RegExp(REF_SOURCE, 'gu');
  const parts = [];
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    const start = m.index + m[1].length;
    const label = m[2];
    if (m[3] && m[3].length > 5) continue; // 6 chiffres : couleur, pas un ticket (cf. en-tête)
    if (start > last) parts.push({ type: 'text', value: text.slice(last, start) });
    if (m[3]) parts.push({ type: 'ticket', number: Number(m[3]), label, href: ticketHref(m[3]) });
    else parts.push({ type: 'deal', number: Number(m[4]), label, href: dealHref(m[4]) });
    last = start + label.length;
  }
  if (last < text.length) parts.push({ type: 'text', value: text.slice(last) });
  return parts;
}

export function hasRefs(text) {
  return splitRefs(text).some((p) => p.type !== 'text');
}

const LINK_STYLE = { color: '#2563eb', textDecoration: 'underline', textUnderlineOffset: 2 };

const TITLES = { ticket: 'Ouvrir le ticket', deal: "Ouvrir l'opportunité" };

/**
 * Lien vers une référence : un <a> simple. Sans écouteur installé (test, aperçu
 * isolé), c'est un lien classique ; dans l'application, `installRefLinkNavigation`
 * le rend interne.
 */
export function RefLink({ part }) {
  return (
    <a href={part.href} style={LINK_STYLE} title={`${TITLES[part.type]} ${part.label}`} data-ref-link={part.type}>
      {part.label}
    </a>
  );
}

/**
 * Texte brut → tableau de nœuds React (ou le texte tel quel s'il n'y a rien à
 * lier : les appelants qui testent `typeof x === 'string'` gardent leur chemin).
 * Accepte aussi un tableau déjà découpé (sortie de `linkifyText`) : seuls ses
 * morceaux TEXTE sont traités, les éléments (liens d'URL, cartes PDF) passent tels quels.
 */
export function linkifyRefs(input, keyPrefix = 'ref') {
  if (Array.isArray(input)) {
    let touched = false;
    const out = [];
    input.forEach((node, i) => {
      if (typeof node === 'string') {
        const sub = linkifyRefs(node, `${keyPrefix}-${i}`);
        if (Array.isArray(sub)) { touched = true; out.push(...sub); return; }
      }
      out.push(node);
    });
    return touched ? out : input;
  }
  const parts = splitRefs(input);
  if (!parts.some((p) => p.type !== 'text')) return input;
  return parts.map((p, i) => (p.type === 'text'
    ? p.value
    : <RefLink key={`${keyPrefix}-${i}`} part={p} />));
}

/** Composant pratique : `<RefText text={ticket.description} />`. */
export function RefText({ text }) {
  if (text === null || text === undefined) return null;
  return <>{linkifyRefs(String(text))}</>;
}

// ── Markdown ────────────────────────────────────────────────────────────────

// Nœuds mdast dont le texte ne doit JAMAIS devenir un lien : un lien déjà posé
// (pas de lien dans un lien) et le code (une commande citée reste littérale).
const MDAST_SKIP = new Set(['link', 'linkReference', 'definition', 'code', 'inlineCode', 'html']);

function walkMdast(node) {
  if (!node || !Array.isArray(node.children) || MDAST_SKIP.has(node.type)) return;
  const next = [];
  node.children.forEach((child) => {
    if (child.type !== 'text') {
      walkMdast(child);
      next.push(child);
      return;
    }
    const parts = splitRefs(child.value);
    if (!parts.some((p) => p.type !== 'text')) {
      next.push(child);
      return;
    }
    parts.forEach((p) => {
      if (p.type === 'text') next.push({ type: 'text', value: p.value });
      // `hProperties` : l'attribut data-ref-link arrive sur le <a> rendu, pour
      // que l'écouteur global rende le clic interne.
      else next.push({
        type: 'link', url: p.href, title: null,
        data: { hProperties: { 'data-ref-link': p.type, title: `${TITLES[p.type]} ${p.label}` } },
        children: [{ type: 'text', value: p.label }],
      });
    });
  });
  node.children = next;
}

/** Plugin remark : transforme les références des nœuds texte en liens. */
export function remarkRefLinks() {
  return (tree) => { walkMdast(tree); };
}

// ── HTML (déjà assaini) ─────────────────────────────────────────────────────

const HTML_SKIP = new Set(['A', 'CODE', 'PRE', 'SCRIPT', 'STYLE', 'TEXTAREA', 'BUTTON']);

function htmlAncestorSkipped(node, root) {
  for (let n = node.parentNode; n && n !== root; n = n.parentNode) {
    if (HTML_SKIP.has(n.nodeName)) return true;
  }
  return false;
}

/**
 * HTML → même HTML, références transformées en `<a data-ref-link>`.
 * On n'ajoute que des liens relatifs construits ici, jamais une valeur venue du
 * texte (le libellé passe par `textContent`). DOMParser n'exécute rien. Appeler
 * AVANT l'assainisseur : c'est lui qui doit rester la dernière étape avant
 * `dangerouslySetInnerHTML`. Le clic est rendu interne par l'écouteur global.
 */
export function linkifyRefsInHtml(html) {
  if (typeof html !== 'string' || html === '' || typeof DOMParser === 'undefined') return html;
  if (!hasRefs(html.replace(/<[^>]*>/g, ' '))) return html;
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstChild;
  const walker = doc.createTreeWalker(root, 4 /* NodeFilter.SHOW_TEXT */);
  const cibles = [];
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    if (!htmlAncestorSkipped(n, root) && hasRefs(n.nodeValue)) cibles.push(n);
  }
  if (cibles.length === 0) return html;
  cibles.forEach((n) => {
    const frag = doc.createDocumentFragment();
    splitRefs(n.nodeValue).forEach((p) => {
      if (p.type === 'text') { frag.appendChild(doc.createTextNode(p.value)); return; }
      const a = doc.createElement('a');
      a.setAttribute('href', p.href);
      a.setAttribute('data-ref-link', p.type);
      a.setAttribute('title', `${TITLES[p.type]} ${p.label}`);
      a.setAttribute('style', 'color:#2563eb;text-decoration:underline;text-underline-offset:2px');
      a.textContent = p.label;
      frag.appendChild(a);
    });
    n.parentNode.replaceChild(frag, n);
  });
  return root.innerHTML;
}

/**
 * Clic sur un lien de référence → navigation interne (pas de rechargement).
 * Ctrl/Cmd/Maj/Alt/clic milieu : comportement natif (nouvel onglet) conservé.
 * La propagation est ARRÊTÉE : une ligne ou une carte cliquable autour du texte
 * (liste de tickets, carte d'opportunité) ne doit pas naviguer ailleurs en même temps.
 */
export function handleRefLinkClick(e, navigate) {
  const a = e.target && e.target.closest ? e.target.closest('a[data-ref-link]') : null;
  if (!a || !navigate) return;
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  e.preventDefault();
  e.stopPropagation();
  navigate(a.getAttribute('href'));
}

/**
 * Pose l'écouteur global (phase de CAPTURE sur `document`, donc avant les
 * gestionnaires React des lignes cliquables). Rend la fonction de retrait.
 * À appeler une fois, depuis un composant placé sous le routeur, avec son `navigate`.
 */
export function installRefLinkNavigation(navigate) {
  if (typeof document === 'undefined') return () => {};
  const onClick = (e) => handleRefLinkClick(e, navigate);
  document.addEventListener('click', onClick, true);
  return () => document.removeEventListener('click', onClick, true);
}
